import { tv, type VariantProps } from 'tailwind-variants'

/**
 * Mapped from the Figma "Foundation / Buttons" set.
 *
 * INTENTS:
 *   filled  → bg #DF6645 (primary), white text, no border
 *   outline → white fill, #99A1AF border (neutral gray), gray-900 text
 *             (the "View Details" button on the loan card)
 *
 * DISABLED (per intent — these are the grid's disabled cells, NOT extra intents):
 *   filled  disabled → #F3F4F6 fill (gray-200 token), gray-700 text, no border
 *   outline disabled → white fill, #E5E7EB border (gray-500 token), gray-700 text
 *
 * No hover styles by design: this is a touch-first mobile PWA, and :hover on
 * touch devices produces sticky/incorrect states. Interaction feedback should
 * come from :active / tap behaviour if needed.
 *
 * SIZES: the grid shows each intent at two heights (~52px lg, ~35px sm),
 * radius ~12px → rounded-sm.
 *
 * `disabled` is a config variant so the look is driven by the same boolean we
 * pass to the native element — the visual and the semantics can't drift.
 *
 * Lives in its own module rather than in Button.tsx because a few elements must
 * look like buttons without being one: OmnidekPortalLink has to be a native <a>
 * to get real new-tab semantics. Sharing the recipe (instead of copying class
 * strings) keeps them from drifting when this is restyled — and a .ts module
 * avoids the react-refresh rule against exporting non-components from a
 * component file.
 */
export const button = tv({
  base: [
    'inline-flex items-center justify-center rounded-sm font-semibold',
    'transition-colors select-none',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
    'disabled:pointer-events-none',
  ],
  variants: {
    intent: {
      filled: 'bg-primary text-white',
      outline: 'bg-white border-2 border-gray-600 text-gray-900',
    },
    size: {
      lg: 'text-base px-5 h-14', // ~52px
      sm: 'text-sm px-4 h-9', // ~36px
    },
    disabled: { true: '' },
    fullWidth: { true: 'w-full' },
  },
  compoundVariants: [
    {
      intent: 'filled',
      disabled: true,
      class: 'bg-gray-200 text-gray-600 border-transparent',
    },
    {
      intent: 'outline',
      disabled: true,
      class: 'bg-white border-gray-500 text-gray-600',
    },
  ],
  defaultVariants: {
    intent: 'filled',
    size: 'lg',
  },
})

export type ButtonVariants = VariantProps<typeof button>
